import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Bank, HandCoins, Money, Receipt, ShoppingCart, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, CHART_COLORS, formatCurrency, fmtErr, inr } from "../lib/api";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { RecordPayment } from "../components/RecordPayment";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payTarget, setPayTarget] = useState(null);

  const loadData = useCallback(() => {
    API.get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Your business at a glance</p>
      </div>

      {data.usage && data.usage.invoice_limit > 0 && data.usage.invoices_used >= data.usage.invoice_limit && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E02424]/30 bg-[#E02424]/10 px-4 py-3" data-testid="upgrade-banner">
          <p className="text-sm font-medium text-[#E02424]">
            You've reached your monthly invoice limit ({data.usage.invoices_used}/{data.usage.invoice_limit}). Upgrade your plan to keep billing without interruption.
          </p>
          <Button size="sm" asChild data-testid="upgrade-banner-button">
            <Link to="/billing?plan=pro">Upgrade Plan</Link>
          </Button>
        </div>
      )}
      {data.usage && data.usage.invoice_limit > 0 && data.usage.invoices_used >= 0.85 * data.usage.invoice_limit && data.usage.invoices_used < data.usage.invoice_limit && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/10 px-4 py-3" data-testid="upgrade-warning">
          <p className="text-sm text-[#B7791F]">
            You've used {data.usage.invoices_used} of {data.usage.invoice_limit} invoices this month — you're close to your plan limit.
          </p>
          <Button size="sm" variant="outline" asChild data-testid="upgrade-warning-button">
            <Link to="/billing?plan=pro">View Plans</Link>
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard title="Sales (This Month)" value={inr(data.sales_month)} icon={Receipt} testId="stat-sales-month" />
        <StatCard title="Purchases (This Month)" value={inr(data.purchases_month)} icon={ShoppingCart} testId="stat-purchases-month" />
        <StatCard title="Expenses (This Month)" value={inr(data.expenses_month)} icon={Money} testId="stat-expenses-month" />
        <StatCard title="Receivables" value={inr(data.receivables)} icon={HandCoins} hint="Customers owe you" testId="stat-receivables" />
        <StatCard title="Payables" value={inr(data.payables)} icon={Wallet} hint="You owe vendors" testId="stat-payables" />
        <StatCard title="Cash & Bank" value={inr(data.bank_total)} icon={Bank} testId="stat-bank-total" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="payments-due-panel">
        {[
          { title: "Payments Due — Receivables", hint: "Customers owe you", rows: data.due_receivables, kind: "sale", testid: "due-receivables", empty: "No pending customer payments." },
          { title: "Payments Due — Payables", hint: "You owe vendors", rows: data.due_payables, kind: "purchase", testid: "due-payables", empty: "No pending vendor bills." },
        ].map((sec) => (
          <div key={sec.kind} className="rounded-lg border bg-card" data-testid={sec.testid}>
            <div className="border-b p-4">
              <h2 className="font-heading text-base font-semibold tracking-tight">{sec.title}</h2>
              <p className="text-xs text-muted-foreground">{sec.hint}</p>
            </div>
            {sec.rows.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">{sec.empty}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>No.</TableHead>
                    <TableHead>Party</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Due</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sec.rows.map((d) => (
                    <TableRow key={d.id} className="transition-colors hover:bg-muted/50" data-testid={`due-row-${d.number}`}>
                      <TableCell className="font-medium">{d.number}</TableCell>
                      <TableCell>{d.party_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{d.date}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-[#E02424]">{inr(d.due)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setPayTarget({ type: sec.kind, partyId: d.party_id, docId: d.id, amount: d.due })} data-testid={`due-record-${d.number}`}>
                          Record
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border bg-card p-6 lg:col-span-2" data-testid="sales-purchases-chart">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Sales vs Purchases</h2>
          <p className="mb-4 text-xs text-muted-foreground">Last 6 months</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly} margin={{ left: 0, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} width={70} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
                <Area type="monotone" dataKey="sales" name="Sales" stroke="#0A2540" fill="#0A2540" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="purchases" name="Purchases" stroke="#B7791F" fill="#B7791F" fillOpacity={0.12} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6" data-testid="expense-chart">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Expense Breakdown</h2>
          <p className="mb-4 text-xs text-muted-foreground">By category</p>
          {data.expense_by_category.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No expenses recorded yet.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.expense_by_category} dataKey="amount" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {data.expense_by_category.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border bg-card" data-testid="recent-invoices">
          <div className="flex items-center justify-between border-b p-4">
            <h2 className="font-heading text-base font-semibold tracking-tight">Recent Invoices</h2>
            <Link to="/sales" className="text-xs font-medium text-primary hover:underline" data-testid="view-all-sales-link">View all</Link>
          </div>
          {data.recent_invoices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-muted-foreground">No invoices yet.</p>
              <Link to="/sales" className="mt-1 inline-block text-sm font-medium text-primary hover:underline">Create your first invoice</Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_invoices.map((inv) => (
                  <TableRow key={inv.id} className="transition-colors hover:bg-muted/50">
                    <TableCell className="font-medium">{inv.number}</TableCell>
                    <TableCell>{inv.party_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(inv.total / (Number(inv.exchange_rate) || 1), inv.currency || "INR")}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="rounded-lg border bg-card" data-testid="top-customers">
          <div className="border-b p-4">
            <h2 className="font-heading text-base font-semibold tracking-tight">Top Customers</h2>
          </div>
          {data.top_customers.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Sales will show your best customers here.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_customers.map((c) => (
                  <TableRow key={c.name}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(c.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="rounded-lg border bg-card" data-testid="low-stock">
          <div className="flex items-center justify-between border-b p-4">
            <h2 className="font-heading text-base font-semibold tracking-tight">Low Stock Alerts</h2>
            <Link to="/inventory" className="text-xs font-medium text-primary hover:underline" data-testid="view-inventory-link">Inventory</Link>
          </div>
          {data.low_stock.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">All items are above their low-stock level.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.low_stock.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-medium">{it.name}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#E02424]">{it.stock} {it.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <RecordPayment
        type="sale"
        preset={payTarget?.type === "sale" ? payTarget : null}
        open={payTarget?.type === "sale"}
        onOpenChange={(v) => !v && setPayTarget(null)}
        onSaved={loadData}
      />
      <RecordPayment
        type="purchase"
        preset={payTarget?.type === "purchase" ? payTarget : null}
        open={payTarget?.type === "purchase"}
        onOpenChange={(v) => !v && setPayTarget(null)}
        onSaved={loadData}
      />
    </div>
  );
}
